import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const UPI_STORAGE_KEY = 'hardayRedeemUpiId';
const EMAIL_STORAGE_KEY = 'hardayRedeemEmail';

// Reward options shown in the Redeem popup (per spec).
// `kind: 'upi'` is the only non-guaranteed option and uses a "Try" button.
const REWARD_OPTIONS = [
  { value: 'myntra', label: 'Myntra Gift Card', kind: 'giftcard' },
  { value: 'flipkart', label: 'Flipkart Gift Card', kind: 'giftcard' },
  { value: 'google_play', label: 'Google Play Gift Card', kind: 'giftcard' },
  { value: 'amazon_pay', label: 'Amazon Pay Gift Card', kind: 'giftcard' },
  { value: 'upi', label: 'UPI', kind: 'upi' },
];

const RedeemRewardPopup = ({ isOpen, onClose }) => {
  const { user, requestWithdrawal, refreshCurrentUser } = useAuth();

  const pendingExists = Boolean(user?.pendingRedeemExists);
  const referralCountEligible = Boolean(user?.rewardBalanceEligible ?? user?.referralCountEligible);

  const [rewardType, setRewardType] = useState('upi');
  const [upiId, setUpiId] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const selectedOption = useMemo(
    () => REWARD_OPTIONS.find((opt) => opt.value === rewardType) || REWARD_OPTIONS[4],
    [rewardType],
  );
  const isUpi = selectedOption.kind === 'upi';

  useEffect(() => {
    if (!isOpen) return;
    setSubmitted(false);
    setSubmitting(false);
    setErrorMessage('');

    // Pre-select the user's last reward type if available, else default to UPI.
    const preferredType = user?.lastRedeemRewardType
      && REWARD_OPTIONS.some((opt) => opt.value === user.lastRedeemRewardType)
      ? user.lastRedeemRewardType
      : 'upi';
    setRewardType(preferredType);

    // Auto-fill UPI / email from server-side last value, fallback to local cache.
    const cachedUpi = (typeof localStorage !== 'undefined' && localStorage.getItem(UPI_STORAGE_KEY)) || '';
    const cachedEmail = (typeof localStorage !== 'undefined' && localStorage.getItem(EMAIL_STORAGE_KEY)) || '';
    setUpiId((user?.lastRedeemUpi || cachedUpi || '').trim());
    setEmail((user?.lastRedeemEmail || cachedEmail || '').trim());
  }, [isOpen, user?.lastRedeemUpi, user?.lastRedeemEmail, user?.lastRedeemRewardType]);

  const inputValue = isUpi ? upiId : email;
  const trimmedInput = inputValue.trim();
  const baseEligible = pendingExists || referralCountEligible;
  const canSubmit = !submitting && trimmedInput.length > 0 && baseEligible;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setErrorMessage('');
    setSubmitting(true);

    const payload = isUpi
      ? { rewardType: 'upi', method: 'upi', upiId: trimmedInput }
      : { rewardType, method: 'giftcard', deliveryEmail: trimmedInput };

    const result = await requestWithdrawal(payload);

    if (!result?.success) {
      setErrorMessage(result?.error || 'Failed to submit redeem request');
      setSubmitting(false);
      return;
    }

    try {
      if (isUpi) {
        localStorage.setItem(UPI_STORAGE_KEY, trimmedInput);
      } else {
        localStorage.setItem(EMAIL_STORAGE_KEY, trimmedInput);
      }
    } catch (err) {
      // ignore storage errors
    }

    setSubmitted(true);
    setSubmitting(false);

    if (typeof refreshCurrentUser === 'function') {
      refreshCurrentUser();
    }

    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 2000);
  };

  const buttonLabel = submitting
    ? 'Submitting...'
    : isUpi
      ? 'Try'
      : pendingExists
        ? 'Update Request'
        : 'Redeem';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 backdrop-blur-md bg-black/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="relative bg-white rounded-[24px] p-6 max-w-md w-full shadow-2xl max-h-[85vh] overflow-y-auto"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
          >
            <button
              onClick={onClose}
              data-testid="redeem-popup-close"
              className="absolute top-4 right-4"
            >
              <X className="w-5 h-5 text-[#666666]" />
            </button>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#000000] font-['Outfit'] mb-2">
                  {pendingExists ? 'Request Updated' : 'Request Submitted'}
                </h2>
                <p className="text-sm text-[#666666] font-['DM_Sans']">
                  {pendingExists
                    ? 'Your existing payout request was updated with the latest details.'
                    : isUpi
                      ? 'Your UPI request was received. We will try to process it.'
                      : 'Your gift card request was received and will be delivered to your email.'}
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#000000] font-['Outfit']">
                    Redeem Reward
                  </h2>
                </div>

                <div className="mb-4">
                  <label className="text-sm text-[#666666] mb-2 block font-['DM_Sans']">
                    Choose your reward
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-testid="reward-type-grid">
                    {REWARD_OPTIONS.map((opt) => {
                      const isActive = rewardType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setRewardType(opt.value)}
                          data-testid={`reward-type-${opt.value}`}
                          className={`text-left rounded-[12px] px-3 py-2.5 text-xs font-semibold font-['DM_Sans'] border transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white border-transparent shadow'
                              : 'bg-white text-[#111111] border-[#E5E7EB] hover:border-[#9333EA]/40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {isUpi && (
                  <div
                    data-testid="upi-warning"
                    className="mb-4 flex items-start gap-2 rounded-[12px] bg-yellow-50 border border-yellow-200 p-3"
                  >
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-[12px] text-yellow-800 font-['DM_Sans']">
                      UPI reward is not guaranteed. You may try or choose another reward
                      option.
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="text-sm text-[#666666] mb-2 block font-['DM_Sans']">
                    {isUpi ? 'UPI ID' : 'Email Address'}
                  </label>
                  {isUpi ? (
                    <input
                      type="text"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value)}
                      data-testid="redeem-upi-input"
                      placeholder="yourname@upi"
                      className="w-full p-3 rounded-[16px] glass-panel text-[#000000] focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                    />
                  ) : (
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="redeem-email-input"
                      placeholder="you@example.com"
                      className="w-full p-3 rounded-[16px] glass-panel text-[#000000] focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                    />
                  )}
                </div>

                {errorMessage && (
                  <p className="mb-3 rounded-[12px] bg-red-50 px-3 py-2 text-xs font-['DM_Sans'] text-red-600">
                    {errorMessage}
                  </p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  data-testid="redeem-submit-btn"
                  className="w-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#EF4444] text-white py-3.5 rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {buttonLabel}
                </button>

                {pendingExists && (
                  <p className="mt-3 text-[11px] text-[#888] font-['DM_Sans'] text-center">
                    A payout request is already pending. New rewards are added to it
                    automatically.
                  </p>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RedeemRewardPopup;
